import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/config";
import { Label } from "./ui/label";
import { Loader2, Sparkles, ArrowLeft, ArrowRight, RotateCcw, ImagePlus, X, Camera } from "lucide-react";
import { toast } from "sonner";
import AreaSelector from "./AreaSelector";

/**
 * iter118 — Advanced add-on WIZARD.
 *
 * Displays ONE question at a time (Back / Next / Restart). Uses the exact
 * same existing pricing engine (`/api/addons/price`) — the wizard only
 * restructures the UX. No pricing rules changed.
 *
 * Props:
 *   serviceKey — one of ADVANCED_ADDON_KEYS
 *   frequency  — cadence from FrequencySchedulePicker (defaults 'one_time')
 *   onQuoteChange({ price, line_items, options, frequency, summary_label, photo_media_ids })
 */
export const ADVANCED_ADDON_KEYS = new Set([
  "leaf_removal",
  "yard_cleanup",
  "flower_bed_weeding",
  "mulching",
  "weeding",
  "hedge_trimming",  // iter122
]);

// Per-service question sequences — labels + testid + option list.
// iter126 — Every wizard now starts with the shared AreaSelector question
// (`type: "area_map"`). The old front/back/both prompts have been removed
// so there's a single source of truth for property location across New
// Booking, Add Service, and Extra Work. The pricing engine reads `areas`
// from options and applies the multi-area multiplier server-side.
const AREA_STEP = {
  id: "areas",
  label: "Which area needs service?",
  type: "area_map",
  required: true,
};

const QUESTIONS = {
  leaf_removal: [
    AREA_STEP,
    { id: "since_last", label: "How many leaves are there?", options: [
      { key: "less_1m",    label: "Light",  sub: "Under 1 month of buildup" },
      { key: "1_3_months", label: "Medium", sub: "1–3 months of buildup" },
      { key: "3_6_months", label: "Heavy",  sub: "3–6 months of buildup" },
      { key: "more_6m",    label: "Severe", sub: "6+ months of buildup" },
    ]},
    { id: "haul_away", label: "Should we haul the debris away?", type: "yesno", positive: "Yes · +$100 disposal", negative: "No" },
  ],
  yard_cleanup: [
    AREA_STEP,
    { id: "overgrown_level", label: "How much cleanup is needed?", options: [
      { key: "light",    label: "Light",    sub: "Tidy up, minor debris" },
      { key: "moderate", label: "Moderate", sub: "Standard yard reset" },
      { key: "heavy",    label: "Heavy",    sub: "Significant overgrowth" },
      { key: "severe",   label: "Severe",   sub: "Neglected / full reset" },
    ]},
    { id: "haul_away", label: "Should we haul the debris away?", type: "yesno", positive: "Yes · +$100 disposal", negative: "No" },
  ],
  flower_bed_weeding: [
    AREA_STEP,
    { id: "remove_weeds",  label: "Remove weeds & grass from the beds?", type: "yesno" },
    { id: "remove_leaves", label: "Remove leaves from the beds?", type: "yesno" },
    { id: "haul_away",     label: "Should we haul the debris away?", type: "yesno", positive: "Yes · +$100 disposal", negative: "No" },
  ],
  mulching: [
    AREA_STEP,
    { id: "mulch_color", label: "What mulch color?", options: [
      { key: "brown",   label: "Brown" }, { key: "red", label: "Red" },
      { key: "black",   label: "Black" }, { key: "cypress", label: "Cypress" },
    ]},
    { id: "remove_old_mulch", label: "Remove the old mulch first?", type: "yesno", positive: "Yes · +$120 labor", negative: "No" },
    { id: "haul_old_mulch",   label: "Haul the old mulch away?", type: "yesno", positive: "Yes · +$100 disposal", negative: "No" },
  ],
  weeding: [
    AREA_STEP,
    { id: "severity", label: "How much weed coverage?", options: [
      { key: "light",    label: "Light" },
      { key: "moderate", label: "Moderate" },
      { key: "heavy",    label: "Heavy" },
    ]},
    { id: "haul_away", label: "Should we haul the debris away?", type: "yesno", positive: "Yes · +$100 disposal", negative: "No" },
  ],
  // iter122 — Bush / Hedge Trimming — count + height (location-only for pricing).
  hedge_trimming: [
    AREA_STEP,
    { id: "bush_count", label: "How many bushes or hedges need trimming?", type: "number",
      min: 1, max: 200, step: 1, unit: "bushes" },
    { id: "height_tier", label: "What is the approximate height?", options: [
      { key: "under_3ft", label: "Under 3 ft" },
      { key: "3_to_6ft",  label: "3–6 ft" },
      { key: "over_6ft",  label: "Over 6 ft" },
    ]},
  ],
};

// iter121 — NO DEFAULTS. The customer must answer every question before
// any price is calculated. Options start empty; each question's answer is
// required to progress. The pricing engine is only called after ALL
// required questions have an explicit customer answer.

const MAX_PHOTOS = 5;

/** True if `val` is a valid customer-provided answer (never treats
 * missing keys or empty strings as "answered"). Booleans are valid
 * answers for yes/no questions (yes = true, no = false). Arrays are
 * valid iff they have at least one element (used by the `area_map` step
 * to force the customer to pick at least one property area). */
function _answered(val) {
  if (val === undefined || val === null) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "string") return val.trim() !== "";
  if (typeof val === "number") return Number.isFinite(val) && val > 0;
  return true; // booleans + non-empty other types count
}

export default function AdvancedAddonForm({ serviceKey, frequency = "one_time", onQuoteChange }) {
  const seq = QUESTIONS[serviceKey] || [];
  const totalSteps = seq.length + 1; // + photos step (photos are optional)
  const [options, setOptions] = useState({});           // iter121: empty, no defaults
  const [step, setStep] = useState(0);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  // Reset entirely when service changes.
  useEffect(() => {
    setOptions({});          // iter121: no prefill
    setStep(0);
    setQuote(null);
    setPhotos([]);
  }, [serviceKey]);

  const set = (patch) => setOptions((prev) => ({ ...prev, ...patch }));

  // iter121 — Every question in `seq` must be answered before pricing.
  const allAnswered = useMemo(
    () => seq.length > 0 && seq.every((q) => _answered(options[q.id])),
    [seq, options],
  );

  const currentAnswered = useMemo(() => {
    const q = seq[step];
    return q ? _answered(options[q.id]) : true; // photos step is optional
  }, [seq, step, options]);

  // iter121 — Live re-pricing runs ONLY after ALL required questions
  // are answered. No premature calls, no estimate shown from defaults.
  useEffect(() => {
    if (!allAnswered) {
      setQuote(null);
      onQuoteChange?.(null);   // let the dialog disable "Add to booking"
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/addons/price", {
          service_key: serviceKey, frequency, options,
        });
        if (!active) return;
        setQuote(data);
        onQuoteChange?.({
          price: data.total,
          line_items: data.line_items,
          options,
          frequency,
          summary_label: data.summary_label,
          photo_media_ids: photos.map((p) => p.media_id),
        });
      } catch (e) {
        if (!active) return;
        setQuote(null);
        onQuoteChange?.(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceKey, frequency, options, photos, allAnswered]);

  const currentQuestion = seq[step];
  const onPhotosStep = step === seq.length;
  const canPrev = step > 0;
  // iter121 — cannot advance past the current question until answered.
  const canNext = step < totalSteps - 1 && currentAnswered;

  const restart = () => {
    if (!window.confirm("Restart? Your current answers and photos will be cleared.")) return;
    setOptions({});           // iter121: no prefill
    setPhotos([]);
    setStep(0);
  };

  const pickPhoto = () => inputRef.current?.click();

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset input so same file can be re-picked
    if (!files.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(`You can upload up to ${MAX_PHOTOS} photos`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of toUpload) {
        if (!/^image\/(jpe?g|png|heic|heif)$/i.test(f.type)) {
          toast.error(`Skipped ${f.name}: only JPG / PNG / HEIC allowed`);
          continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`Skipped ${f.name}: over 10 MB`);
          continue;
        }
        const data_url = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(f);
        });
        const { data } = await api.post("/quotes/upload/photo", { data_url });
        uploaded.push({ media_id: data.media_id, data_url });
      }
      if (uploaded.length) {
        setPhotos((prev) => [...prev, ...uploaded]);
        toast.success(`${uploaded.length} photo${uploaded.length === 1 ? "" : "s"} attached`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid={`addon-wizard-${serviceKey}`}>
      {/* Progress dots */}
      <div className="flex items-center gap-1.5" data-testid="addon-wizard-progress">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i}
                className={`h-1.5 flex-1 rounded-full transition ${i < step ? "bg-emerald-500" : i === step ? "bg-emerald-400" : "bg-gray-200"}`} />
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-gray-500" data-testid="addon-wizard-step-label">
        Step {step + 1} of {totalSteps}
      </div>

      {/* Question body */}
      {!onPhotosStep && currentQuestion && (
        <QuestionStep
          q={currentQuestion}
          value={options[currentQuestion.id]}
          onChange={(v) => set({ [currentQuestion.id]: v })}
        />
      )}
      {onPhotosStep && (
        <PhotosStep
          photos={photos}
          onPick={pickPhoto}
          onRemove={removePhoto}
          uploading={uploading}
          fileInputRef={inputRef}
          onFilesChosen={onFilesChosen}
        />
      )}

      {/* Nav */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={!canPrev}
                data-testid="addon-wizard-back"
                className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.14em] px-3 h-9 rounded-full border border-gray-300 text-gray-700 disabled:opacity-40 hover:bg-gray-50">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" onClick={restart}
                data-testid="addon-wizard-restart"
                className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.14em] px-3 h-9 rounded-full text-rose-600 hover:bg-rose-50">
          <RotateCcw className="w-3.5 h-3.5" /> Restart
        </button>
        <button type="button" onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                disabled={!canNext}
                data-testid="addon-wizard-next"
                className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.14em] px-4 h-9 rounded-full bg-[#047857] hover:bg-[#065F46] text-white disabled:opacity-40">
          Next <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* iter121 — Price card ONLY appears after every required question
          has a customer-provided answer. Never shows a placeholder price. */}
      {allAnswered ? (
        <div className="rounded-2xl bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white p-5 relative overflow-hidden" data-testid={`addon-price-card-${serviceKey}`}>
          <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-emerald-400/15 blur-2xl" />
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-extrabold">
            <Sparkles className="w-3 h-3" /> Per-visit price
          </div>
          <div className="mt-1 text-4xl font-extrabold tabular-nums tracking-tighter font-[Outfit]" data-testid={`addon-price-${serviceKey}`}>
            {loading ? <Loader2 className="w-7 h-7 animate-spin inline" /> : quote ? `$${quote.total.toFixed(2)}` : "—"}
          </div>
          {quote && (
            <ul className="mt-3 space-y-1 text-[12px] text-emerald-100/95" data-testid={`addon-lineitems-${serviceKey}`}>
              {quote.line_items.map((li, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="truncate">{li.label}</span>
                  <span className="tabular-nums font-extrabold">
                    {li.amount > 0 ? `+$${li.amount.toFixed(2)}` : "included"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {photos.length > 0 && (
            <div className="mt-3 text-[10px] uppercase tracking-[0.14em] text-emerald-200 font-extrabold" data-testid="addon-photos-summary">
              {photos.length} photo{photos.length === 1 ? "" : "s"} attached
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center" data-testid={`addon-price-locked-${serviceKey}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-extrabold">Per-visit price</div>
          <div className="mt-1 text-sm font-bold text-gray-700">
            Answer every question above to see your price.
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            {seq.filter((q) => _answered(options[q.id])).length} of {seq.length} answered
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= Question step ================= */
function QuestionStep({ q, value, onChange }) {
  const answered = _answered(value);
  return (
    <div data-testid={`addon-wizard-q-${q.id}`}>
      <Label className="text-[15px] font-extrabold text-gray-900 leading-tight block">
        {q.label}
      </Label>
      {q.type === "area_map" ? (
        // iter127 — Yard Area picker (Front / Back / Front + Back).
        // No internal header — the wizard step's `q.label` above is the title.
        <div className="mt-3">
          <AreaSelector
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
            showHeader={false}
          />
        </div>
      ) : q.type === "number" ? (
        <NumberStepper q={q} value={value} onChange={onChange} />
      ) : q.type === "yesno" ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {/* iter121 — Neither Yes nor No is highlighted until the customer
              explicitly picks one. `active` is only true after selection. */}
          <ChipButton active={answered && value === false} onClick={() => onChange(false)}
                       testid={`addon-wizard-${q.id}-no`}>{q.negative || "No"}</ChipButton>
          <ChipButton active={answered && value === true} onClick={() => onChange(true)}
                       testid={`addon-wizard-${q.id}-yes`}>{q.positive || "Yes"}</ChipButton>
        </div>
      ) : (
        <div className={`mt-3 grid ${q.options.length >= 4 ? "grid-cols-2" : "grid-cols-3"} gap-2`}>
          {q.options.map((o) => (
            <ChipButton key={o.key} active={answered && value === o.key}
                         onClick={() => onChange(o.key)}
                         testid={`addon-wizard-${q.id}-${o.key}`}>
              <span className="text-[13px] font-extrabold">{o.label}</span>
              {o.sub && <span className="block text-[10px] font-normal opacity-80 mt-0.5 normal-case tracking-normal">{o.sub}</span>}
            </ChipButton>
          ))}
        </div>
      )}
      {!answered && (
        <div className="mt-2 text-[11px] text-amber-700 font-bold" data-testid={`addon-wizard-${q.id}-required`}>
          {q.type === "area_map"
            ? "Please choose at least one area on the map to continue."
            : q.type === "number"
              ? "Please enter a value to continue."
              : "Please choose an option to continue."}
        </div>
      )}
    </div>
  );
}


/* ================= Number stepper (iter122) ================= */
function NumberStepper({ q, value, onChange }) {
  const min = q.min ?? 1, max = q.max ?? 200, step = q.step ?? 1;
  const cur = Number.isFinite(value) ? value : "";
  const dec = () => onChange(Math.max(min, (Number(cur) || min) - step));
  const inc = () => onChange(Math.min(max, (Number(cur) || 0) + step));
  return (
    <div className="mt-3">
      <div className="flex items-center gap-3">
        <button type="button" onClick={dec} disabled={typeof cur === "number" && cur <= min}
                 data-testid={`addon-wizard-${q.id}-dec`}
                 className="w-12 h-12 rounded-full border-2 border-[#047857] text-[#047857] font-extrabold text-2xl leading-none disabled:opacity-40 hover:bg-emerald-50">−</button>
        <input
          type="number"
          inputMode="numeric"
          min={min} max={max} step={step}
          value={cur === "" ? "" : cur}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") { onChange(undefined); return; }
            const n = parseInt(raw, 10);
            if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
          }}
          placeholder={`Enter ${q.unit || "quantity"}`}
          data-testid={`addon-wizard-${q.id}-input`}
          className="flex-1 h-12 rounded-xl border-2 border-gray-200 focus:border-[#047857] focus:outline-none px-4 text-center text-2xl font-extrabold tabular-nums"
        />
        <button type="button" onClick={inc} disabled={typeof cur === "number" && cur >= max}
                 data-testid={`addon-wizard-${q.id}-inc`}
                 className="w-12 h-12 rounded-full border-2 border-[#047857] text-[#047857] font-extrabold text-2xl leading-none disabled:opacity-40 hover:bg-emerald-50">+</button>
      </div>
      <div className="mt-1 text-[11px] text-gray-500 text-center">
        {min}–{max} {q.unit || ""}
      </div>
    </div>
  );
}

/* ================= Photos step ================= */
function PhotosStep({ photos, onPick, onRemove, uploading, fileInputRef, onFilesChosen }) {
  return (
    <div data-testid="addon-wizard-photos-step">
      <Label className="text-[15px] font-extrabold text-gray-900 leading-tight block">
        Attach 1–5 photos <span className="text-gray-400 font-bold">(optional)</span>
      </Label>
      <p className="mt-1 text-[12px] text-gray-500">Photos help our crew give you an accurate estimate. JPG · PNG · HEIC · up to 10 MB each.</p>
      <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
        {photos.map((p, i) => (
          <div key={p.media_id} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50" data-testid={`addon-wizard-photo-${i}`}>
            <img src={p.data_url} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover" />
            <button type="button" onClick={() => onRemove(i)}
                     data-testid={`addon-wizard-photo-remove-${i}`}
                     className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <button type="button" onClick={onPick} disabled={uploading}
                   data-testid="addon-wizard-photo-add"
                   className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 flex flex-col items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em]">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImagePlus className="w-5 h-5" /> Add</>}
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif"
              onChange={onFilesChosen} className="hidden"
              data-testid="addon-wizard-file-input" />
      <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-500">
        <Camera className="w-3 h-3" /> {photos.length} of {MAX_PHOTOS} attached
      </div>
    </div>
  );
}

/* ================= Shared UI ================= */
function ChipButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 min-h-[52px] py-2 rounded-xl text-[13px] font-extrabold border transition text-left ${
        active
          ? "border-[#047857] text-white bg-[#047857] shadow-sm"
          : "border-gray-200 text-gray-700 bg-white hover:border-emerald-300"
      }`}
    >
      {children}
    </button>
  );
}
